import React, {useEffect, useContext} from 'react';
import {
  BrowserRouter,
  Route,
  Redirect,
  Link,
  NavLink as RRNavLink,
  Switch,
  withRouter,
} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import * as R from 'ramda';
import {NavLink} from 'reactstrap';

import i18n, {availableLocales} from '@/i18n';
import usePermission from '@/hooks/usePermission';

import App from '@/App';
import {SystemConfigCtx} from '@/contexts';

// layout
import {
  B2c as B2cLayout,
  B2b as B2bLayout,
  LoginPage as LoginPageLayout,
  Public as PublicLayout,
  Internal as InternalLayout,
} from '@/views/Layout';

import Error404 from '@/Error404';

const LocaleLink = ({to, ...props}) => {
  const {i18n: i18nCtx} = useTranslation();

  return <Link to={`/${i18nCtx.language}${to === '/' ? '' : to}`} {...props} />;
};

const LocaleNavLink = ({to, ...props}) => {
  const {i18n: i18nCtx} = useTranslation();

  return (
    <NavLink
      tag={RRNavLink}
      exact
      to={`/${i18nCtx.language}${to === '/' ? '' : to}`}
      activeClassName='active'
      {...props}
    />
  );
};

const localesPath = `/:locale(${R.join('|', availableLocales)})?`;

const RouterApp = () => (
  <BrowserRouter basename={process.env.REACT_APP_BASENAME}>
    <Route path={localesPath} component={App} />
  </BrowserRouter>
);

const ProtectedRoute = ({
  component: Component,
  referrer,
  enabled = () => true,
  validator = () => false,
  renderInvalid = ({location}) => {
    const lang = i18n.language;
    const state = referrer === false ? undefined : {referrer: location};
    return <Redirect to={{pathname: `/${lang}/login`, state}} />;
  },
  ...rest
}) => {
  if (!enabled()) return <Error404 />;

  return (
    <Route
      {...rest}
      render={props => {
        if (validator()) return <Component {...props} />;
        return renderInvalid(props);
      }}
    />
  );
};

const useChangeLangEffect = match => {
  const requestedLang = R.pathOr('en', ['params', 'locale'], match);
  useEffect(
    () => {
      i18n.changeLanguage(requestedLang);
    },
    [requestedLang]
  );
};

const useChangeSiteCodeEffect = () => {
  const {siteCode, siteName} = useContext(SystemConfigCtx);
  useEffect(
    () => {
      document.title = siteName;
      document.getElementById('favicon').href = `${
        process.env.PUBLIC_URL
      }/favicon_${siteCode}.ico`;
    },
    [siteName]
  );
};

const ComposedSwitch = ({match}) => {
  useChangeLangEffect(match);
  useChangeSiteCodeEffect();

  const {
    canAccessB2BPages,
    canAccessB2CPages,
    canAccessInternalPages,
    enableInternalPages,
    enableExternalPages,
  } = usePermission();

  return (
    <Switch>
      <ProtectedRoute
        path={`${match.path}/internal`}
        component={InternalLayout}
        validator={canAccessInternalPages}
        enabled={enableInternalPages}
        renderInvalid={() => {
          window.location.href = process.env.REACT_APP_AMOEBA_URL;
          return null;
        }}
      />
      <ProtectedRoute
        path={`${match.path}/b2b`}
        component={B2bLayout}
        enabled={enableExternalPages}
        validator={canAccessB2BPages}
      />
      <ProtectedRoute
        path={`${match.path}/b2c`}
        component={B2cLayout}
        enabled={enableExternalPages}
        validator={canAccessB2CPages}
      />
      <ProtectedRoute
        path={`${match.path}/login`}
        enabled={enableExternalPages}
        component={LoginPageLayout}
        validator={() => true}
      />
      <ProtectedRoute
        path={`${match.path}/`}
        exact
        enabled={enableExternalPages}
        component={LoginPageLayout}
        validator={() => true}
      />
      <ProtectedRoute
        path={`${match.path}`}
        enabled={enableExternalPages}
        component={PublicLayout}
        validator={() => true}
      />
      <Route path='*' component={Error404} />
    </Switch>
  );
};

const RouterSwitch = withRouter(ComposedSwitch);

export {LocaleLink, LocaleNavLink, RouterApp, RouterSwitch, localesPath};
